import { Component, computed, effect, inject, output, signal } from '@angular/core';
import { Router } from '@angular/router';

import { AssetsService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { GLOBAL_CONSTANT } from '@lp-ui-lib';
import { BUILDING_LAYERS, filterStatus } from '@lp-ui-lib';
import { groupBy } from '@lp-ui-lib';
import { Icons } from '@lp-ui-lib';
import { App_Routes } from '@lp-ui-lib';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NameSlicePipe } from '@lp-ui-lib';
import { AssetNavButtonComponent } from '@lp-ui-lib';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CardComponent } from '../../../components/card/card';

@Component({
  selector: 'app-plant-list',
  imports: [
    NzAlertModule,
    NameSlicePipe,
    AssetNavButtonComponent,
    BreadcrumbsComponent,
    CardComponent,
    NzIconModule,
  ],
  templateUrl: './plant-list.html',
  styleUrls: ['./plant-list.scss'],
})
export class PlantListComponent {
  // Inject services
  private assetsService = inject(AssetsService);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  private router = inject(Router);

  loadingThumbnails = signal(false);

  public filterStatusEnum = filterStatus;

  public statusFilterValue = output<string>();

  plants = computed(() =>
    this.assetsService.plantsState().plants.filter((p) => p.curLayer === BUILDING_LAYERS.C)
  );

  loading = computed(() => !this.assetsService.plantsState().loadingComplete);
  filteredPlants = computed(() => this.assetsService.plantsState().filtered);
  visiblePlants = computed(() => {
    return this.plants().filter((plant: any) => this.assetsForPlant(plant).length > 0);
  });

  bcGroupedPlants = computed(() => {
    const grouped = groupBy(this.visiblePlants(), 'parentID');

    return this.sortByLocation(grouped);
  });

  sumCritical = computed(() =>
    this.plants().reduce((sum: any, p: any) => sum + p.Critical.length, GLOBAL_CONSTANT.ZERO)
  );
  sumWarning = computed(() =>
    this.plants().reduce((sum: any, p: any) => sum + p.Warning.length, GLOBAL_CONSTANT.ZERO)
  );
  sumInactive = computed(() =>
    this.plants().reduce((sum: any, p: any) => sum + p.InActive.length, GLOBAL_CONSTANT.ZERO)
  );
  sumWorking = computed(() =>
    this.plants().reduce((sum: any, p: any) => sum + p.Working.length, GLOBAL_CONSTANT.ZERO)
  );

  sumArchived = computed(() =>
    this.plants().reduce((sum, plant) => sum + plant.Archived.length, GLOBAL_CONSTANT.ZERO)
  );
  // Computed signals
  activeFilter = computed(() => this.assetsService.getFilter());

  assetCount = computed(() => {
    return this.visiblePlants().reduce(
      (total: number, plant: any) => total + this.assetsForPlant(plant).length,
      0
    );
  });
  // assetCount = computed(
  //   () =>
  //     this.plants()
  //       .map((plant) => plant.Assets)
  //       .flat(2)
  //       .filter((asset: any) => !asset.isArchived).length
  // );

  isMobile = computed(() => this.ui.IsMobile());

  isTablet = computed(() => this.ui.IsTablet());

  isDesktop = computed(() => this.ui.IsDesktop());

  allWorking = computed(
    () =>
      this.assetCount() > 0 &&
      this.sumWorking() > 0 &&
      this.sumCritical() === 0 &&
      this.sumInactive() === 0 &&
      this.sumWarning() === 0
  );

  // Effect to subscribe to orientation changes
  orientationEffect = effect(() => {
    this.ui.OrientaionChange();
  });

  // Sort by location name
  private sortByLocation(countryArray: any[][]): any[][] {
    return countryArray.sort((a: any, b: any) => {
      const nameA = this.i18n.parse(a[0].breadcrumbs[1].objectName).toUpperCase();
      const nameB = this.i18n.parse(b[0].breadcrumbs[1].objectName).toUpperCase();
      return nameA.localeCompare(nameB);
    });
  }

  assetsGroupBy(plant: any): any[][] {
    return groupBy(this.assetsForPlant(plant), 'parentID');
  }

  private matchesActiveFilter(asset: any, filter: any): boolean {
    if (!asset || !filter) {
      return false;
    }

    const isDeInstalled = asset.isArchived === true || asset.deInstalled === true;

    // Status filter
    let matchesStatus = true;

    if (filter.deInstalled === true) {
      matchesStatus = isDeInstalled;
    } else if (filter.inactive === true) {
      matchesStatus = !isDeInstalled && asset.inactive === true;
    } else if (filter.critical === true) {
      matchesStatus = !isDeInstalled && asset.inactive !== true && asset.critical === true;
    } else if (filter.warning === true) {
      matchesStatus =
        !isDeInstalled &&
        asset.inactive !== true &&
        asset.critical !== true &&
        asset.warning === true;
    } else if (filter.working === true) {
      matchesStatus =
        !isDeInstalled &&
        asset.inactive !== true &&
        asset.critical !== true &&
        asset.warning !== true;
    }

    if (!matchesStatus) {
      return false;
    }

    // Equipment-type filter
    const selectedTypes: string[] = filter.selectedTypes ?? [];

    if (selectedTypes.length > 0) {
      const equipmentType = this.normalizeEquipmentType(asset.equipmentType);

      const matchesEquipmentType = selectedTypes.some(
        (selectedType: string) => this.normalizeEquipmentType(selectedType) === equipmentType
      );

      if (!matchesEquipmentType) {
        return false;
      }
    }

    // Optional customer filter
    if (filter.customer && asset.customerName !== filter.customer) {
      return false;
    }

    return true;
  }

  private normalizeEquipmentType(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private assetsForPlant(plant: any): any[] {
    const filter = this.activeFilter();

    return (plant?.Assets ?? []).filter((asset: any) => this.matchesActiveFilter(asset, filter));
  }

  statusChecked(asset: any): string {
    if (asset.critical && !asset.inactive) return Icons.STAT_EXPLANATION_TRIANGLE;
    if (asset.inactive) return Icons.CROSS_CIRCLE;
    if (asset.warning) return Icons.STAT_EXPLANATION_OCTAGON;
    if (asset.isArchived) return Icons.DISCONNECTED;
    return Icons.STATUS_CHECK;
  }

  onClick(plant: { objectID: any }, filterStatus: any): void {
    setTimeout(() => {
      if (plant) {
        this.router.navigate([App_Routes.PLANT, plant.objectID], {
          state: { status: filterStatus },
        });
      }
    });
  }

  statusClick(filterStatus: any): void {
    this.statusFilterValue.emit(filterStatus);
  }
}
